<template>
  <ConfigPageShell
    title="Feed Field Rules"
    subtitle='Transform field values from any feed before they reach your live inventory. Example: if vAuto Features contains "sunroof", replace it with "moon roof".'
    dense
  >
    <template #actions>
      <v-btn
        color="primary"
        variant="flat"
        :disabled="!isDirty || isSaving"
        :loading="isSaving"
        @click="save"
      >
        Save Draft
      </v-btn>
    </template>

      <!-- Loading -->
      <div v-if="isLoading" class="d-flex justify-center py-8">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Error -->
      <v-alert v-if="!isLoading && loadError" type="error" variant="tonal" class="mb-4">
        {{ loadError }}
      </v-alert>

      <template v-if="!isLoading && !loadError">
        <v-alert type="info" variant="tonal" class="mb-4">
          Rules are evaluated <strong>top to bottom</strong> within each feed. All matching
          rules are applied in order. Transformations run after normal field mapping.
        </v-alert>

        <!-- Feed tabs -->
        <div class="d-flex align-center mb-4 flex-wrap ga-2">
          <v-tabs v-model="activeTab" color="primary">
            <v-tab
              v-for="feedConfig in feedConfigs"
              :key="feedConfig.feed"
              :value="feedConfig.feed"
            >
              {{ getFeedLabel(feedConfig.feed) }}
              <v-chip
                size="x-small"
                class="ml-2"
                :color="feedConfig.rules.length ? 'primary' : 'grey'"
              >
                {{ feedConfig.rules.length }}
              </v-chip>
            </v-tab>
          </v-tabs>
          <v-btn
            variant="tonal"
            color="primary"
            prepend-icon="mdi-plus"
            size="small"
            @click="showAddFeedDialog = true"
          >
            Add Feed
          </v-btn>
        </div>

        <!-- Empty state -->
        <div v-if="feedConfigs.length === 0" class="text-center py-12 text-grey">
          <v-icon size="48" class="mb-3">mdi-transform</v-icon>
          <div class="text-body-1 mb-2">No feed rules configured yet</div>
          <div class="text-body-2 mb-4">
            Click "Add Feed" to start configuring field transformation rules.
          </div>
          <v-btn color="primary" prepend-icon="mdi-plus" @click="showAddFeedDialog = true">
            Add Feed
          </v-btn>
        </div>

        <!-- Feed rule panels -->
        <v-window v-model="activeTab">
          <v-window-item
            v-for="feedConfig in feedConfigs"
            :key="feedConfig.feed"
            :value="feedConfig.feed"
          >
            <v-card variant="outlined" class="mb-4">
              <v-card-title class="d-flex align-center justify-space-between">
                <span>{{ getFeedLabel(feedConfig.feed) }} Rules</span>
                <v-btn
                  variant="text"
                  color="error"
                  size="small"
                  prepend-icon="mdi-delete-outline"
                  @click="removeFeed(feedConfig.feed)"
                >
                  Remove Feed
                </v-btn>
              </v-card-title>

              <v-card-text>
                <div
                  v-if="feedConfig.rules.length === 0"
                  class="text-body-2 text-grey text-center py-4"
                >
                  No rules for {{ getFeedLabel(feedConfig.feed) }}. Add a rule below.
                </div>

                <!-- Rule cards -->
                <v-card
                  v-for="(rule, ruleIdx) in feedConfig.rules"
                  :key="ruleIdx"
                  variant="tonal"
                  class="mb-3 pa-3"
                >
                  <!-- Rule header -->
                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="d-flex align-center flex-wrap ga-2">
                      <v-chip size="small" color="grey-darken-1" variant="flat">
                        Rule {{ ruleIdx + 1 }}
                      </v-chip>
                      <span class="text-body-2">when</span>
                      <v-btn-toggle
                        v-model="rule.logic"
                        density="compact"
                        variant="outlined"
                        color="secondary"
                      >
                        <v-btn value="AND" size="x-small">ALL match</v-btn>
                        <v-btn value="OR" size="x-small">ANY match</v-btn>
                      </v-btn-toggle>
                    </div>
                    <div class="d-flex">
                      <v-btn
                        icon="mdi-arrow-up"
                        size="x-small"
                        variant="text"
                        :disabled="ruleIdx === 0"
                        @click="moveRule(feedConfig.feed, ruleIdx, -1)"
                      />
                      <v-btn
                        icon="mdi-arrow-down"
                        size="x-small"
                        variant="text"
                        :disabled="ruleIdx === feedConfig.rules.length - 1"
                        @click="moveRule(feedConfig.feed, ruleIdx, 1)"
                      />
                      <v-btn
                        icon="mdi-delete-outline"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="removeRule(feedConfig.feed, ruleIdx)"
                      />
                    </div>
                  </div>

                  <!-- Match criteria (WHEN) -->
                  <div class="text-caption text-grey-darken-1 font-weight-medium mb-2 ml-4">
                    CONDITIONS
                  </div>
                  <div
                    v-for="(match, matchIdx) in rule.match"
                    :key="matchIdx"
                    class="d-flex align-center mb-2 ml-4 flex-wrap ga-2"
                  >
                    <v-combobox
                      v-model="match.field"
                      :items="getFeedSourceFields(feedConfig.feed)"
                      item-title="label"
                      item-value="value"
                      :return-object="false"
                      label="Source field"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 180px; max-width: 220px"
                    />

                    <v-select
                      v-model="match.operator"
                      :items="MATCH_OPERATORS"
                      item-title="label"
                      item-value="value"
                      label="Operator"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="max-width: 180px"
                    />

                    <v-combobox
                      v-if="match.operator === 'in'"
                      :model-value="Array.isArray(match.value) ? match.value : []"
                      @update:model-value="match.value = $event ?? []"
                      label="Values"
                      density="compact"
                      variant="outlined"
                      hide-details
                      multiple
                      chips
                      closable-chips
                      style="min-width: 240px"
                    />
                    <v-text-field
                      v-else
                      :model-value="String(match.value ?? '')"
                      label="Value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 240px"
                      @update:model-value="match.value = String($event ?? '')"
                    />

                    <v-btn
                      icon="mdi-close"
                      size="x-small"
                      variant="text"
                      color="error"
                      :disabled="rule.match.length <= 1"
                      @click="removeMatch(feedConfig.feed, ruleIdx, matchIdx)"
                    />
                  </div>

                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    prepend-icon="mdi-plus"
                    class="ml-4 mb-4"
                    @click="addMatch(feedConfig.feed, ruleIdx)"
                  >
                    Add condition
                  </v-btn>

                  <v-divider class="mb-3" />

                  <!-- Action (THEN) -->
                  <div class="text-caption text-grey-darken-1 font-weight-medium mb-2 ml-4">
                    ACTION
                  </div>
                  <div class="d-flex align-center flex-wrap ga-3 ml-4">
                    <!-- Target SRP field -->
                    <v-combobox
                      v-model="rule.targetField"
                      :items="srpFields"
                      item-title="label"
                      item-value="value"
                      label="Target SRP field"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 200px; max-width: 240px"
                      @update:model-value="onTargetFieldChange(rule, $event)"
                    />

                    <v-select
                      v-model="rule.action.type"
                      :items="TRANSFORM_ACTIONS"
                      item-title="label"
                      item-value="value"
                      label="Action"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="max-width: 180px"
                    />

                    <template v-if="rule.action.type === 'replace'">
                      <v-text-field
                        v-model="rule.action.find"
                        label="Find"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width: 150px"
                      />
                      <span class="text-body-2 text-grey">→</span>
                      <v-text-field
                        v-model="rule.action.replaceWith"
                        label="Replace with"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="min-width: 150px"
                      />
                    </template>

                    <v-text-field
                      v-else-if="rule.action.type === 'set'"
                      v-model="rule.action.value"
                      label="New value"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 200px"
                    />

                    <v-text-field
                      v-else-if="rule.action.type === 'append' || rule.action.type === 'remove'"
                      v-model="rule.action.value"
                      :label="rule.action.type === 'append' ? 'Value to append' : 'Value to remove'"
                      density="compact"
                      variant="outlined"
                      hide-details
                      style="min-width: 200px"
                    />
                  </div>

                  <!-- Summary chip -->
                  <div class="ml-4 mt-3">
                    <v-chip size="small" color="deep-orange" variant="tonal">
                      {{ describeRule(rule) }}
                    </v-chip>
                  </div>
                </v-card>

                <v-btn
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-plus"
                  @click="addRule(feedConfig.feed)"
                >
                  Add Rule
                </v-btn>
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>
      </template>

      <!-- Snackbars -->
      <v-snackbar v-model="showSaveSuccess" color="success" :timeout="4000">
        Feed field rules saved as draft. Click "Publish Changes" to apply to live inventory.
      </v-snackbar>
      <v-snackbar v-model="showSaveError" color="error" :timeout="4000">
        Failed to save feed field rules. Please try again.
      </v-snackbar>
    </ConfigPageShell>

    <!-- Add feed dialog -->
    <v-dialog v-model="showAddFeedDialog" max-width="400">
      <v-card>
        <v-card-title>Add Feed</v-card-title>
        <v-card-text>
          <v-select
            v-model="newFeedValue"
            :items="availableFeeds"
            item-title="label"
            item-value="value"
            label="Select feed"
            variant="outlined"
            density="compact"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="showAddFeedDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!newFeedValue"
            @click="addFeed"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { ref, computed, watch } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { usePublishStore } from '@/stores/usePublishStore'
import {
  fetchFeedFieldRules,
  fetchAccountFeeds,
  saveFeedFieldRulesDraft,
  createEmptyFeedConfig,
  createEmptyRule,
  createEmptyMatch,
  getFeedLabel as getFeedLabelBase,
  KNOWN_FEEDS,
  MATCH_OPERATORS,
  TRANSFORM_ACTIONS,
  type FeedFieldRuleConfig,
  type FeedFieldRule,
  type AccountFeedFieldRules,
} from '@/services/feedFieldRulesService'

const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()
const iseEndpoint = String(import.meta.env.VITE_ISE_BACKEND_URL || '').replace(/\/+$/g, '')

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const isLoading = ref(false)
const isSaving = ref(false)
const loadError = ref<string | null>(null)
const showSaveSuccess = ref(false)
const showSaveError = ref(false)

const feedConfigs = ref<FeedFieldRuleConfig[]>([])
const savedSnapshot = ref('')
const activeTab = ref<string | null>(null)

const showAddFeedDialog = ref(false)
const newFeedValue = ref<string | null>(null)

const srpFields = ref<{ value: string; label: string }[]>([])
const feedSourceFieldsCache = ref<Record<string, { value: string; label: string }[]>>({})
const accountFeeds = ref<{ value: string; label: string }[]>([])

// ---------------------------------------------------------------------------
// Derived
// ---------------------------------------------------------------------------

const isDirty = computed(() => JSON.stringify(feedConfigs.value) !== savedSnapshot.value)

const feedList = computed(() =>
  accountFeeds.value.length > 0 ? accountFeeds.value : KNOWN_FEEDS,
)

const availableFeeds = computed(() =>
  feedList.value.filter(f => !feedConfigs.value.some(c => c.feed === f.value)),
)

function getFeedLabel(feedValue: string): string {
  return getFeedLabelBase(feedValue, accountFeeds.value)
}

// ---------------------------------------------------------------------------
// Field discovery
// ---------------------------------------------------------------------------

async function loadSrpFields() {
  try {
    const accountId = String(accountSettings.accountId || '').trim()
    const token = String(accountSettings.sessionToken || '').trim()
    if (!iseEndpoint || !accountId || !token) return

    const url = `${iseEndpoint}/mapping-config/fields?juid=${encodeURIComponent(accountId)}`
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    if (!resp.ok) return
    const json = await resp.json()

    const data: Array<{ key: string }> = json?.data ?? []
    srpFields.value = data
      .filter(f => f.key)
      .map(f => ({
        value: f.key,
        label: f.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch {
    // non-fatal — user can type a field name manually
  }
}

async function loadAccountFeeds() {
  try {
    const accountId = String(accountSettings.accountId || '').trim()
    const token = String(accountSettings.sessionToken || '').trim()
    if (!iseEndpoint || !accountId || !token) return
    accountFeeds.value = await fetchAccountFeeds(iseEndpoint, accountId, token)
  } catch {
    // non-fatal — falls back to KNOWN_FEEDS
  }
}

function getFeedSourceFields(feed: string): { value: string; label: string }[] {
  return feedSourceFieldsCache.value[feed] ?? []
}

async function loadFeedSourceFields(feed: string) {
  if (feedSourceFieldsCache.value[feed]) return
  try {
    const token = String(accountSettings.sessionToken || '').trim()
    const accountId = String(accountSettings.accountId || '').trim()
    if (!iseEndpoint || !token) return
    const url = `${iseEndpoint}/mapping-config/feed-fields?feed=${encodeURIComponent(feed)}&juid=${encodeURIComponent(accountId)}`
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    if (!resp.ok) return
    const json = await resp.json()
    const fields: string[] = json?.data?.fields ?? []
    feedSourceFieldsCache.value[feed] = fields
      .filter(Boolean)
      .map(f => ({ value: f, label: f }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch {
    feedSourceFieldsCache.value[feed] = []
  }
}

// ---------------------------------------------------------------------------
// Load / Save
// ---------------------------------------------------------------------------

async function load() {
  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()
  if (!iseEndpoint || !accountId || !token) return

  isLoading.value = true
  loadError.value = null

  try {
    const [data] = await Promise.all([
      fetchFeedFieldRules(iseEndpoint, accountId, token),
      loadSrpFields(),
      loadAccountFeeds(),
    ])
    feedConfigs.value = data.feeds.length > 0 ? structuredClone(data.feeds) : []
    snapshotSaved()

    for (const fc of feedConfigs.value) {
      loadFeedSourceFields(fc.feed)
    }

    if (feedConfigs.value.length > 0 && !activeTab.value) {
      activeTab.value = feedConfigs.value[0].feed
    }
  } catch (err: unknown) {
    loadError.value = err instanceof Error ? err.message : 'Failed to load feed field rules'
  } finally {
    isLoading.value = false
  }
}

async function save() {
  const accountId = String(accountSettings.accountId || '').trim()
  const token = String(accountSettings.sessionToken || '').trim()
  if (!iseEndpoint || !accountId || !token) return

  isSaving.value = true
  try {
    const normalized = feedConfigs.value.map(fc => ({
      ...fc,
      rules: fc.rules.map(rule => ({
        ...rule,
        match: rule.match.map(m => ({
          ...m,
          field: typeof m.field === 'object' && m.field !== null ? (m.field as any).value ?? String(m.field) : m.field,
        })),
      })),
    }))
    const payload: AccountFeedFieldRules = { feeds: normalized }
    const ok = await saveFeedFieldRulesDraft(iseEndpoint, accountId, token, payload)
    if (ok) {
      snapshotSaved()
      showSaveSuccess.value = true
      await publishStore.refreshUnpublishedChangesSummary()
    } else {
      showSaveError.value = true
    }
  } catch {
    showSaveError.value = true
  } finally {
    isSaving.value = false
  }
}

function snapshotSaved() {
  savedSnapshot.value = JSON.stringify(feedConfigs.value)
}

// ---------------------------------------------------------------------------
// Feed management
// ---------------------------------------------------------------------------

function addFeed() {
  if (!newFeedValue.value) return
  feedConfigs.value.push(createEmptyFeedConfig(newFeedValue.value))
  activeTab.value = newFeedValue.value
  loadFeedSourceFields(newFeedValue.value)
  newFeedValue.value = null
  showAddFeedDialog.value = false
}

function removeFeed(feed: string) {
  const idx = feedConfigs.value.findIndex(f => f.feed === feed)
  if (idx === -1) return
  feedConfigs.value.splice(idx, 1)
  activeTab.value = feedConfigs.value[0]?.feed ?? null
}

// ---------------------------------------------------------------------------
// Rule management
// ---------------------------------------------------------------------------

function addRule(feed: string) {
  feedConfigs.value.find(f => f.feed === feed)?.rules.push(createEmptyRule())
}

function removeRule(feed: string, ruleIdx: number) {
  feedConfigs.value.find(f => f.feed === feed)?.rules.splice(ruleIdx, 1)
}

function moveRule(feed: string, ruleIdx: number, direction: -1 | 1) {
  const fc = feedConfigs.value.find(f => f.feed === feed)
  if (!fc) return
  const target = ruleIdx + direction
  if (target < 0 || target >= fc.rules.length) return
  ;[fc.rules[ruleIdx], fc.rules[target]] = [fc.rules[target], fc.rules[ruleIdx]]
}

function addMatch(feed: string, ruleIdx: number) {
  feedConfigs.value.find(f => f.feed === feed)?.rules[ruleIdx]?.match.push(createEmptyMatch())
}

function removeMatch(feed: string, ruleIdx: number, matchIdx: number) {
  feedConfigs.value.find(f => f.feed === feed)?.rules[ruleIdx]?.match.splice(matchIdx, 1)
}

function onTargetFieldChange(rule: FeedFieldRule, selected: unknown) {
  if (selected && typeof selected === 'object' && 'value' in selected && 'label' in selected) {
    rule.targetField = (selected as { value: string; label: string }).value
    rule.targetFieldLabel = (selected as { value: string; label: string }).label
  } else {
    rule.targetField = String(selected ?? '')
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

function describeRule(rule: FeedFieldRule): string {
  const target = rule.targetFieldLabel || rule.targetField || '?'
  switch (rule.action.type) {
    case 'replace':
      return `On "${target}": replace "${rule.action.find ?? ''}" → "${rule.action.replaceWith ?? ''}"`
    case 'set':
      return `Set "${target}" to "${rule.action.value ?? ''}"`
    case 'append':
      return `Append "${rule.action.value ?? ''}" to "${target}"`
    case 'remove':
      return `Remove "${rule.action.value ?? ''}" from "${target}"`
    default:
      return target
  }
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

watch(
  () => [accountSettings.accountId, accountSettings.sessionToken],
  () => { load() },
  { immediate: true },
)
</script>
